import { type ReactNode, useEffect, useRef, useState } from "react";
import { CheckmarkIcon, ChevronIcon, Search01Icon } from "vega-icons";

import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@vega-core/components/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type OptionGroup = {
  label: string;
  options: OptionType[];
};

export type SearchableDropdownProps = {
  name: string;
  label?: string;
  labelTag?: ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  value?: OptionType;
  onChange?: (value: OptionType | undefined) => void;
  onAction?: (value: string) => void;
  options?: OptionType[];
  groups?: OptionGroup[];
  disabled?: boolean;
  error?: string;
  renderValue?: (label: string) => ReactNode;
  trigger?: ReactNode;
  popoverAlign?: "center" | "end" | "start";
  popoverClassName?: string;
  className?: string;
  onInputChange?: (value: string) => void;
  inputValue?: string;
  /** When false, parent is expected to provide an already-filtered `options`/`groups` set. */
  shouldFilter?: boolean;
  isLoading?: boolean;
  optionClassName?: string;
  wrapOptionLabels?: boolean;
  withChevron?: boolean;
  withCheckmark?: boolean;
  withSearchIcon?: boolean;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
};

function LoadMoreSentinel({
  name,
  onLoadMore,
  isLoadingMore,
}: {
  name: string;
  onLoadMore: () => void;
  isLoadingMore: boolean;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [onLoadMore, isLoadingMore]);

  return (
    <div
      className="px-3 py-2 text-text/secondary body-extra-small-regular text-center"
      data-testid={`${name}-searchable-dropdown-load-more-sentinel`}
      ref={sentinelRef}
    >
      {isLoadingMore ? m.common_loading() : null}
    </div>
  );
}

function DropdownItem({
  option,
  name,
  onSelect,
  optionClassName,
  wrapOptionLabels,
  isSelected,
}: {
  option: OptionType;
  name: string;
  onSelect: () => void;
  optionClassName?: string;
  wrapOptionLabels?: boolean;
  isSelected: boolean;
}) {
  return (
    <CommandItem
      className={cnTwMerge(
        "cursor-pointer hover:bg-background/secondary",
        { "opacity-50 cursor-not-allowed": option.disabled },
        optionClassName,
      )}
      data-testid={`${name}-searchable-dropdown-option-${option.value}`}
      disabled={option.disabled}
      keywords={[option.label]}
      onSelect={() => !option.disabled && onSelect()}
      value={option.value}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isTruthy(option.icon) && (
          <span className="text-text/secondary shrink-0">{option.icon}</span>
        )}
        <div className="flex flex-col flex-1 min-w-0">
          <span className={wrapOptionLabels ? undefined : "truncate"}>
            {option.label}
          </span>
          {isTruthy(option.description) && (
            <span className="truncate text-text/secondary body-small-regular">
              {option.description}
            </span>
          )}
        </div>
        {isSelected ? (
          <CheckmarkIcon className="h-4 w-4 shrink-0 text-text/interactive" />
        ) : null}
      </div>
    </CommandItem>
  );
}

export function SearchableDropdown({
  name,
  label,
  labelTag,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  onAction,
  options,
  groups,
  disabled = false,
  error,
  renderValue,
  trigger,
  popoverAlign = "start",
  popoverClassName,
  className,
  onInputChange,
  inputValue,
  shouldFilter = true,
  isLoading = false,
  optionClassName,
  wrapOptionLabels = false,
  withChevron = false,
  withCheckmark = false,
  withSearchIcon = false,
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
}: SearchableDropdownProps) {
  const [open, setOpen] = useState(false);

  const selectedLabel = value?.label || "";

  const allOptions = groups
    ? groups.flatMap((group) => group.options)
    : (options ?? []);

  const handleSelect = (optionValue: string) => {
    if (onAction) {
      onAction(optionValue);
      setOpen(false);

      return;
    }

    const selectedOption = allOptions.find((opt) => opt.value === optionValue);
    const newValue =
      selectedOption?.value === value?.value ? undefined : selectedOption;
    onChange?.(newValue);
    setOpen(false);
  };

  const getDisplayText = () => {
    if (!value) {
      return placeholder || m.formFields_dropdown_placeholder();
    }

    if (renderValue) {
      return renderValue(selectedLabel);
    }

    return selectedLabel;
  };

  const renderOptions = () => {
    if (groups) {
      const lastIndex = groups.length - 1;

      return groups.map((group, index) => (
        <CommandGroup
          className="[&_[cmdk-group-heading]]:text-text/secondary [&_[cmdk-group-heading]]:body-extra-small-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide"
          heading={group.label}
          key={group.label}
        >
          {group.options.map((option) => (
            <DropdownItem
              isSelected={withCheckmark ? option.value === value?.value : false}
              key={option.value}
              name={name}
              onSelect={() => handleSelect(option.value)}
              option={option}
              optionClassName={optionClassName}
              wrapOptionLabels={wrapOptionLabels}
            />
          ))}
          {index < lastIndex && (
            <div className="mt-1 -mx-1 h-px bg-border/primary" />
          )}
        </CommandGroup>
      ));
    }

    return (
      <CommandGroup>
        {allOptions.map((option) => (
          <DropdownItem
            isSelected={withCheckmark ? option.value === value?.value : false}
            key={option.value}
            name={name}
            onSelect={() => handleSelect(option.value)}
            option={option}
            optionClassName={optionClassName}
            wrapOptionLabels={wrapOptionLabels}
          />
        ))}
      </CommandGroup>
    );
  };

  return (
    <div className={cnTwMerge("w-full", className)}>
      {isTruthy(label) && (
        <FormLabel
          className={cnTwMerge("cursor-pointer", {
            "opacity-50": disabled,
          })}
          label={label}
          tag={labelTag}
        />
      )}

      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          {trigger ?? (
            <button
              aria-expanded={open}
              className={cnTwMerge(
                "flex h-11 w-full items-center justify-between rounded-companyBorderRadius border border-border/primary bg-background/primary px-3 py-2 body-base-regular text-text/primary transition hover:border-brand-default focus:border-brand-default focus:outline-none focus:ring-0",
                {
                  "border-border/negative": isTruthy(error),
                  "cursor-not-allowed bg-background/secondary opacity-50":
                    disabled,
                  "text-text/disabled": !value,
                },
              )}
              data-testid={`${name}-searchable-dropdown-trigger`}
              disabled={disabled}
              role="combobox"
              type="button"
            >
              <span className="truncate">{getDisplayText()}</span>
              {withChevron ? (
                <ChevronIcon
                  className={cnTwMerge(
                    "h-4 w-4 shrink-0 text-text/secondary defaultTransition",
                    { "rotate-180": open, "opacity-50": disabled },
                  )}
                />
              ) : null}
            </button>
          )}
        </PopoverTrigger>

        {open ? (
          <PopoverContent
            align={popoverAlign}
            className={cnTwMerge(
              "w-[--radix-popover-trigger-width] p-0",
              popoverClassName,
            )}
          >
            <Command
              filter={(value, search, keywords) => {
                const searchLabel = keywords?.[0] ?? "";

                return searchLabel.toLowerCase().includes(search.toLowerCase())
                  ? 1
                  : 0;
              }}
              shouldFilter={shouldFilter}
            >
              <div className="relative">
                {withSearchIcon ? (
                  <Search01Icon
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text/secondary"
                    data-testid={`${name}-searchable-dropdown-search-icon`}
                  />
                ) : null}
                <CommandInput
                  className={withSearchIcon ? "pl-9" : undefined}
                  data-testid={`${name}-searchable-dropdown-search`}
                  onValueChange={onInputChange}
                  placeholder={
                    searchPlaceholder || m.formFields_dropdown_search()
                  }
                  value={inputValue}
                />
              </div>
              <CommandList>
                {isLoading && allOptions.length > 0 ? (
                  <div
                    className="px-3 py-2 text-text/secondary body-extra-small-regular border-b border-border/primary"
                    data-testid={`${name}-searchable-dropdown-loading`}
                  >
                    {m.common_loading()}
                  </div>
                ) : null}
                <CommandEmpty>
                  <span
                    data-testid={
                      isLoading
                        ? `${name}-searchable-dropdown-loading`
                        : undefined
                    }
                  >
                    {isLoading
                      ? m.common_loading()
                      : m.formFields_dropdown_noOptions()}
                  </span>
                </CommandEmpty>
                {renderOptions()}
                {onLoadMore && hasNextPage ? (
                  <LoadMoreSentinel
                    isLoadingMore={isLoadingMore}
                    name={name}
                    onLoadMore={onLoadMore}
                  />
                ) : null}
              </CommandList>
            </Command>
          </PopoverContent>
        ) : null}
      </Popover>

      {isTruthy(error) && (
        <ValidationErrorText dataTestId={`${name}-error`} withMargin>
          {error}
        </ValidationErrorText>
      )}
    </div>
  );
}
